import { useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { markSession } from './pwa/installPrompt';
import { InstallSheet } from './pwa/InstallSheet';
import { resubscribeOnBoot } from './pwa/pushPrompt';
import { setMustRenameHandler, setUnauthorizedHandler, retryUnlessNotFound } from './api/client';
import { useMustRenameStore } from './shell/mustRenameStore';
import { Toast } from './shell/Toast';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: retryUnlessNotFound } } });

function App() {
  // Memoized (not `router()` called fresh in JSX every render, as before): a stable instance is
  // needed to call its imperative `.navigate()` from the auth-expiry effect below.
  const appRouter = useMemo(() => router(), []);

  // §11.11 install-prompt trigger, half 2 of 2 (half 1 is the first-Look-saved
  // `markLookSaved()` call in WardrobeScreen.handleLookSaved): records today as a distinct
  // session day, once per app boot. `installPrompt.ts`'s own `beforeinstallprompt` capture is a
  // module-level side effect that runs at import time (see that file's header comment) — this
  // effect only owns the session-counting half of the trigger.
  useEffect(() => { markSession(); }, []);

  // Task 23, ruling 7: re-post the current push subscription (fresh tz offset) on every boot,
  // but ONLY when the OS permission is already granted — this never itself triggers the
  // permission prompt (see pushPrompt.ts's own doc comment on resubscribeOnBoot).
  useEffect(() => { void resubscribeOnBoot(); }, []);

  // Fix-wave item 10: global auth-expiry handling. client.ts's apiFetch calls this whenever an
  // authenticated-area request 401/403s after a session was previously known-valid in this tab
  // (see that file's own doc comment for the full bootstrap-call exclusion list) — navigate
  // straight to /login with the same `{ reason: 'session-expired' }` location state MeScreen's
  // own pre-existing dedicated 401 handling already uses, so every "your session ended" path in
  // the app lands on the identical screen/note.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      appRouter.navigate('/login', { state: { reason: 'session-expired' } });
    });
    return () => setUnauthorizedHandler(null);
  }, [appRouter]);

  // Task 15 (Decision #10, recon option 2): the mustRename latch, wired via client.ts's own
  // setMustRenameHandler seam — the same shape as the session-expiry handler just above. This
  // effect only ever WRITES the store; AppShell reads it to decide whether to redirect (a
  // declarative render-time check, not an imperative one-shot navigate() here), which is what
  // makes the guard re-assert itself on back-navigation (SCREENS.md "/claim-handle" "non-
  // dismissible") without any extra plumbing.
  useEffect(() => {
    setMustRenameHandler((mustRename) => useMustRenameStore.getState().setMustRename(mustRename));
    return () => setMustRenameHandler(null);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={appRouter} />
      <InstallSheet />
      <Toast />
    </QueryClientProvider>
  );
}

export default App;
